import React, { useEffect, useRef, useState } from 'react'
import OtpInputField from './OTPInputField'
import SuccessPopup from './SuccessPopup'
import { useApp } from '../../../context/usePortal'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../../components/ui/button'

interface DeleteAccountProps {
  onClose: () => void
}

const DeleteAccount: React.FC<DeleteAccountProps> = ({ onClose }) => {
  const { user, getCode, deleteAccount, logout } = useApp()

  const [step, setStep] = useState<'confirm' | 'otp' | 'done'>('confirm')
  const [otp, setOtp] = useState('')
  const [otpAttempt, setOtpAttempt] = useState(0)
  const [seconds, setSeconds] = useState(60)
  const [timerActive, setTimerActive] = useState(false)
  const [status, setStatus] = useState<
    'idle' | 'verifying' | 'failed' | 'succeed'
  >('idle')
  const [resetOtp, setResetOtp] = useState(false)
  const navigate = useNavigate()
  const phoneNumber = user?.country_code + '-' + user?.user_phone_number
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [message, setMessage] = useState<string | undefined>('')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  useEffect(() => {
    let interval: NodeJS.Timeout
    if (timerActive && seconds > 0) {
      interval = setInterval(() => setSeconds((s) => s - 1), 1000)
    } else if (seconds <= 0) {
      setTimerActive(false)
    }
    return () => clearInterval(interval)
  }, [timerActive, seconds])

  const handleSendOTP = async () => {
    setStep('otp')

    const { response } = await getCode(phoneNumber, 'delete_account')
    setMessage(
      response?.message?.includes(':')
        ? response?.message?.split(':')[1]
        : response?.message,
    )
    if (response?.success) {
      setOtp('')
      setOtpAttempt(0)
      setStatus('idle')
      setSeconds(60)
      setTimerActive(true)
      setResetOtp(true)
    }
  }

  const handleDeleteAccount = async () => {
    if (otp.length !== 6) return
    setOtpAttempt((prev) => prev + 1)
    setStatus('verifying')

    const { response } = await deleteAccount(phoneNumber, otp)
    setMessage(
      response?.message?.includes(':')
        ? response?.message?.split(':')[1]
        : response?.message,
    )
    if (response?.success) {
      setStatus('succeed')
      setStep('done')
      setTimeout(() => {
        logout()
        navigate('/login')
      }, 1000)
    } else {
      setStatus('failed')
      if (otpAttempt >= 3) setTimerActive(false)
      setResetOtp((prev) => !prev)
    }
  }

  const censorNumber = (input: string) => {
    const digits = input.replace(/\D/g, '')
    if (digits.length < 4) return input
    return input.replace(new RegExp(`\\d(?=\\d{${4}})`, 'g'), '*')
  }

  if (step === 'done') return <SuccessPopup onClose={onClose} type="resetPIN" />

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-5">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg ">
        {step === 'confirm' && (
          <>
            <h2 className="mb-4 text-center text-xl font-bold">
              Delete Account
            </h2>
            <p className="mb-6 text-center text-sm text-gray-700">
              Are you sure you want to delete your account? This action is
              irreversible.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleSendOTP}
              >
                Yes, Delete
              </Button>
            </div>
          </>
        )}

        {step === 'otp' && (
          <>
            <h2 className="mb-2 text-center text-xl font-bold">
              OTP Verification
            </h2>
            <p className="mb-4 text-center text-sm">
              Enter the 6-digit OTP sent to your phone:{' '}
              {censorNumber(user?.user_phone_number ?? '')}
            </p>

            <OtpInputField
              length={6}
              onChangeOtp={setOtp}
              editable={status === 'verifying'}
              resetOtp={resetOtp}
            />

            {message}

            {seconds > 0 && (
              <p className="mt-2 text-center text-xs">
                Resend OTP in <span className="font-bold">{seconds}s</span>
              </p>
            )}

            <button
              className={`mt-5 w-full rounded-full px-4 py-3 font-bold ${
                status === 'verifying'
                  ? 'bg-green-700 text-white'
                  : 'bg-red-600 text-white'
              }`}
              disabled={status === 'verifying'}
              onClick={handleDeleteAccount}>
              {status === 'verifying' ? 'Verifying...' : 'Delete Account'}
            </button>

            {seconds <= 0 && (
              <button
                className="mt-3 w-full text-sm text-green-700 underline"
                onClick={handleSendOTP}>
                Resend OTP
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default DeleteAccount
